import { toast } from 'react-toastify';
import {
  createAsyncThunk,
  createSlice
} from '@reduxjs/toolkit';

import { API_ENDPOINT } from '../constants';
import { fetchWrapper } from '../helpers';
import { ClinicalStatus } from '../admin/constants/general';

import type {
  ActionReducerMapBuilder,
  PayloadAction,
  SerializedError} from '@reduxjs/toolkit';
import type {
  IClient,
  IClientAuthorization,
  IClientFacilityMap,
  IClientFamily,
  IClientModifierMatrixRow,
  IClientPolicyMap,
  ICreateClientModifierMatrixRowArg,
  IDeleteClientModifierMatrixRowArg,
  IFetchClientModifierMatrixArg,
  IPriorAuthorizationDetail,
  IUpdateClientModifierMatrixRowArg,
} from '../utils/types';
import type {
  IClientDetailsFulfilledAction,
  IClientDetailsPendingAction,
  IClientDetailsRejectedAction,
  IClientDetailsRequest,
} from './types';

const name = 'clients';
const initialState = createInitialState();
const reducers = createReducers();
// SAFETY: Legacy thunk action creators use the store dispatch result contract.
const extraActions = createExtraActions() as any;
const slice = createSlice({
  name,
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<ClientState>) => createExtraReducers(builder),
});

export const buildGetClientUrl = (
  clinical_status: ClinicalStatus | Array<ClinicalStatus>,
  baseUrl: string,
) =>
  Array.isArray(clinical_status)
    ? clinical_status.reduce((url, status, index) => {
        return `${url}${index === 0 ? `?clinical_status=${status}` : `&clinical_status=${status}`}`;
      }, `${baseUrl}/`)
    : `${baseUrl}/?clinical_status=${clinical_status}`;

export interface ClientState {
  list: Array<IClient>;
  allClientsList: Array<IClient>;
  selected: IClient | null;
  selectedClientDetailsRequestClientId: number | null;
  selectedPolicyPriorAuthorizationDetails: IPriorAuthorizationDetail | null;
  paDetailsLoading: boolean;
  error: any;
  loading: boolean;
  assignedFacility: any;
  assignedPayor: any;
  clientsFamily: IClientFamily[];
  clientFamilyLoading: boolean;
  clientFacilitiesMapping: Array<IClientFacilityMap>;
  clientPayorsMapping: Array<IClientPolicyMap>;
  selectedClientPlans: Array<IClientPolicyMap>;
  selectedClientPolicyLoading: boolean;
  selectedClientFacility: Array<IClientFacilityMap>;
  facilityAssignmentsLoading: boolean;
  selectedClientAuthorization: Array<IClientAuthorization>;
  selectedClintAuthloading: boolean;
  selectedClientModifierMatrix: Array<IClientModifierMatrixRow>;
  modifierMatrixLoading: boolean;
  modifierMatrixError: boolean;
  // requestId = latest dispatched fetch (drop stale/out-of-order responses, incl. same client).
  // clientId = which client the currently-stored data belongs to (so the hook never surfaces
  // another client's data). Both are needed: requestId for latest-wins, clientId for ownership.
  modifierMatrixRequestId: string | null;
  modifierMatrixClientId: string | null;
}

function createInitialState(): ClientState {
  return {
    list: [],
    allClientsList: [],
    loading: false,
    error: null,
    selected: null,
    selectedClientDetailsRequestClientId: null,
    assignedFacility: null,
    assignedPayor: null,
    clientsFamily: [],
    selectedPolicyPriorAuthorizationDetails: null,
    paDetailsLoading: false,
    clientFamilyLoading: false,
    clientFacilitiesMapping: [],
    clientPayorsMapping: [],
    selectedClientPlans: [],
    selectedClientPolicyLoading: false,
    selectedClientFacility: [],
    facilityAssignmentsLoading: false,
    selectedClientAuthorization: [],
    selectedClintAuthloading: false,
    selectedClientModifierMatrix: [],
    modifierMatrixLoading: false,
    modifierMatrixError: false,
    modifierMatrixRequestId: null,
    modifierMatrixClientId: null,
  };
}

function createReducers() {
  return {
    clearSelectedClient,
    setSelected,
    updateState,
  };

  function updateState(state: ClientState, action: any) {
    const { key, value } = action.payload;
    Object.assign(state, { [key]: value });
  }

  function clearSelectedClient(state: ClientState) {
    state.selected = null;
    state.selectedClientDetailsRequestClientId = null;
    state.selectedClientPlans = [];
    state.selectedClientFacility = [];
    state.selectedClientAuthorization = [];
    state.selectedClientModifierMatrix = [];
    state.modifierMatrixLoading = false;
    state.modifierMatrixError = false;
    state.modifierMatrixRequestId = null;
    state.modifierMatrixClientId = null;
    state.selectedPolicyPriorAuthorizationDetails = null;
  }

  function setSelected(state: ClientState, action: PayloadAction<IClient>) {
    state.selected = action.payload;
    state.selectedClientDetailsRequestClientId = null;
  }
}

function createExtraActions() {
  const baseUrl = `${API_ENDPOINT}/clients`;

  return {
    getList: getList(),
    createClient: createClient(),
    updateClientClinicalStatus: updateClientClinicalStatus(),
    assignFacility: assignFacility(),
    updateFacilityAssignment: updateFacilityAssignment(),
    deleteFacilityAssignment: deleteFacilityAssignment(),
    assignPayor: assignPayor(),
    getClientFacilitiesMapping: getClientFacilitiesMapping(),
    getClientPayorsMapping: getClientPayorsMapping(),
    getClient: getClient(),
    getSelectedClientDetails: getSelectedClientDetails(),
    getClientsFamily: getClientsFamily(),
    getClientSpecificFacilityMapping: getClientSpecificFacilityMapping(),
    getClientSpecificPayorsMapping: getClientSpecificPayorsMapping(),
    createAuthorization: createAuthorization(),
    getAuthorization: getAuthorization(),
    editPolicy: editPolicy(),
    deactiveClientFacilityAssignment: deactiveClientFacilityAssignment(),
    promoteClient: promoteClient(),
    resetAssessmentAllocation: resetAssessmentAllocation(),
    updateClient: updateClient(),
    getPolicyPriorAuthorizationDetails: getPolicyPriorAuthorizationDetails(),
    fetchClientModifierMatrix: fetchClientModifierMatrix(),
    createClientModifierMatrixRow: createClientModifierMatrixRow(),
    updateClientModifierMatrixRow: updateClientModifierMatrixRow(),
    deleteClientModifierMatrixRow: deleteClientModifierMatrixRow(),
  };

  function getList() {
    return createAsyncThunk(
      `${name}/getList`,
      async ({ clinical_status, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.get({
          url: clinical_status ? buildGetClientUrl(clinical_status, baseUrl) : `${baseUrl}/`,
          getAccessTokenSilently,
        });

        // Sort response data
        const sortedResponse = response?.sort((clientA: IClient, clientB: IClient) =>
          (clientA?.first_name + clientA?.last_name)?.toLowerCase() <
          (clientB?.first_name + clientB?.last_name)?.toLowerCase()
            ? -1
            : 1,
        );

        if (!clinical_status || clinical_status === ClinicalStatus.CLINICAL) {
          return { sortedResponse, type: ClinicalStatus.CLINICAL };
        } else {
          return { sortedResponse };
        }
      },
    );
  }

  function getClientsFamily() {
    return createAsyncThunk(
      `${name}/getClientsFamily`,
      async ({ getAccessTokenSilently, dateActive, client_id }: any) => {
        let url = `${baseUrl}/families/?date_active=${dateActive}`;
        if (client_id) {
          url = `${url}&client_id=${client_id}`;
        }
        const response = await fetchWrapper.get({
          url,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function createClient() {
    return createAsyncThunk(
      `${name}/createClient`,
      async ({ data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          { url: `${baseUrl}/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }

  function updateClientClinicalStatus() {
    return createAsyncThunk(
      `${name}/updateClientClinicalStatus`,
      async ({ data, getAccessTokenSilently, clientId }: any) => {
        const response = await fetchWrapper.put(
          { url: `${baseUrl}/${clientId}/clinical_status/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }

  function assignFacility() {
    return createAsyncThunk(
      `${name}/assignFacility`,
      async ({ data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          { url: `${API_ENDPOINT}/client_facility_assignments/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }

  function updateFacilityAssignment() {
    return createAsyncThunk(
      `${name}/updateFacilityAssignment`,
      async ({ clientFacilityAssignmentId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${API_ENDPOINT}/client_facility_assignments/${clientFacilityAssignmentId}/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function deleteFacilityAssignment() {
    return createAsyncThunk(
      `${name}/deleteFacilityAssignment`,
      async ({ clientFacilityAssignmentId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.delete({
          url: `${API_ENDPOINT}/client_facility_assignments/${clientFacilityAssignmentId}/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function assignPayor() {
    return createAsyncThunk(
      `${name}/assignPayor`,
      async ({ data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          {
            url: `${API_ENDPOINT}/policies/`,
            getAccessTokenSilently,
          },
          data['payload'],
        );
        return response;
      },
    );
  }

  function getPolicyPriorAuthorizationDetails() {
    return createAsyncThunk(
      `${name}/getPolicyPriorAuthorizationDetails`,
      async ({
        policyId,
        serviceCode,
        getAccessTokenSilently,
      }: {
        policyId: number | string;
        serviceCode: number | string;
        getAccessTokenSilently: () => Promise<string>;
      }) => {
        const response = await fetchWrapper.get({
          url: `${API_ENDPOINT}/policies/${policyId}/prior_authorization_details/${serviceCode}/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function editPolicy() {
    return createAsyncThunk(
      `${name}/editPolicy`,
      async ({ policyId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${API_ENDPOINT}/policies/${policyId}/`,
            getAccessTokenSilently,
          },
          data['payload'],
        );
        return response;
      },
    );
  }

  function getClientFacilitiesMapping() {
    return createAsyncThunk(
      `${name}/clientFacilitiesMapping`,
      async ({ getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.get({
          url: `${API_ENDPOINT}/client_facility_assignments/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }
  function getClientPayorsMapping() {
    return createAsyncThunk(
      `${name}/clientPayorsMapping`,
      async ({ getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.get({
          url: `${API_ENDPOINT}/policies/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }
  function getClientSpecificFacilityMapping() {
    return createAsyncThunk(
      `${name}/clientSpecificBFMapping`,
      async ({ getAccessTokenSilently, clientId }: any) => {
        const response = await fetchWrapper.get({
          url: `${API_ENDPOINT}/client_facility_assignments/?client_id=${clientId}`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }
  function getClientSpecificPayorsMapping() {
    return createAsyncThunk(
      `${name}/clientSpecificPayorsMapping`,
      async ({ getAccessTokenSilently, clientId }: any) => {
        const response = await fetchWrapper.get({
          url: `${API_ENDPOINT}/policies/?client_id=${clientId}`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }
  function getClient() {
    return createAsyncThunk(
      `${name}/getClient`,
      async ({ getAccessTokenSilently, clientId }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/${clientId}/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function getSelectedClientDetails() {
    return createAsyncThunk(
      `${name}/getSelectedClientDetails`,
      async ({ getAccessTokenSilently, clientId }: IClientDetailsRequest) => {
        const response: IClient = await fetchWrapper.get({
          url: `${baseUrl}/${clientId}/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function getAuthorization() {
    return createAsyncThunk(
      `${name}/getAuthorization`,
      async ({ getAccessTokenSilently, policyId, clientId }: any) => {
        const response = await fetchWrapper.get({
          url: `${API_ENDPOINT}/policies/${policyId}/authorizations/?client_id=${clientId}`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }
  function createAuthorization() {
    return createAsyncThunk(
      `${name}/createAuthorization`,
      async ({ getAccessTokenSilently, policyId, data, clientId }: any) => {
        const response = await fetchWrapper.post(
          {
            url: `${API_ENDPOINT}/policies/${policyId}/authorizations/?client_id=${clientId}`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function deactiveClientFacilityAssignment() {
    return createAsyncThunk(
      `${name}/deactiveClientFacilityAssignment`,
      async ({ getAccessTokenSilently, assignmentId, data }: any) => {
        const response = await fetchWrapper.put(
          {
            url: `${API_ENDPOINT}/client_facility_assignments/${assignmentId}/deactivate/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function promoteClient() {
    return createAsyncThunk(
      `${name}/promoteClient`,
      async ({ getAccessTokenSilently, clientId }: any) => {
        const response = await fetchWrapper.put({
          url: `${API_ENDPOINT}/clients/${clientId}/promote/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function resetAssessmentAllocation() {
    return createAsyncThunk(
      `${name}/resetAssessmentAllocation`,
      async ({ getAccessTokenSilently, data, clientId }: any) => {
        const response = await fetchWrapper.put(
          {
            url: `${API_ENDPOINT}/clients/${clientId}/reset_assessment_allocation/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function updateClient() {
    return createAsyncThunk(
      `${name}/updateClient`,
      async ({ getAccessTokenSilently, clientId, data }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${API_ENDPOINT}/clients/${clientId}/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function fetchClientModifierMatrix() {
    return createAsyncThunk(
      `${name}/fetchClientModifierMatrix`,
      async ({ getAccessTokenSilently, clientId }: IFetchClientModifierMatrixArg) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/${clientId}/modifier_matrix/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function createClientModifierMatrixRow() {
    return createAsyncThunk(
      `${name}/createClientModifierMatrixRow`,
      async ({ data, getAccessTokenSilently, clientId }: ICreateClientModifierMatrixRowArg) => {
        const response = await fetchWrapper.post(
          { url: `${baseUrl}/${clientId}/modifier_matrix/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }

  function updateClientModifierMatrixRow() {
    return createAsyncThunk(
      `${name}/updateClientModifierMatrixRow`,
      async ({
        data,
        getAccessTokenSilently,
        clientId,
        rowId,
      }: IUpdateClientModifierMatrixRowArg) => {
        const response = await fetchWrapper.put(
          { url: `${baseUrl}/${clientId}/modifier_matrix/${rowId}/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }

  function deleteClientModifierMatrixRow() {
    return createAsyncThunk(
      `${name}/deleteClientModifierMatrixRow`,
      async ({ getAccessTokenSilently, clientId, rowId }: IDeleteClientModifierMatrixRowArg) => {
        const response = await fetchWrapper.delete({
          url: `${baseUrl}/${clientId}/modifier_matrix/${rowId}/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }
}

function createExtraReducers(builder: ActionReducerMapBuilder<ClientState>) {
  const {
    getList,
    createClient,
    assignFacility,
    updateClientClinicalStatus,
    updateFacilityAssignment,
    deleteFacilityAssignment,
    assignPayor,
    getClientFacilitiesMapping,
    getClientPayorsMapping,
    getClient,
    getSelectedClientDetails,
    getClientsFamily,
    getClientSpecificFacilityMapping,
    getClientSpecificPayorsMapping,
    createAuthorization,
    getAuthorization,
    editPolicy,
    deactiveClientFacilityAssignment,
    promoteClient,
    resetAssessmentAllocation,
    updateClient,
    getPolicyPriorAuthorizationDetails,
    fetchClientModifierMatrix,
    createClientModifierMatrixRow,
    updateClientModifierMatrixRow,
    deleteClientModifierMatrixRow,
  } = extraActions;

  builder
    .addCase(getList.pending, (state: ClientState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(getList.fulfilled, (state: ClientState, action: any) => {
      if (action.payload.type === ClinicalStatus.CLINICAL) {
        state.list = action.payload.sortedResponse;
      } else {
        state.allClientsList = action.payload.sortedResponse;
      }
      state.error = null;
      state.loading = false;
    })
    .addCase(getList.rejected, (state: ClientState, action: any) => {
      state.list = [];
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })
    // getClientsFamily()
    .addCase(getClientsFamily.pending, (state: ClientState) => {
      state.clientFamilyLoading = true;
      state.error = null;
    })
    .addCase(
      getClientsFamily.fulfilled,
      (state: ClientState, action: PayloadAction<IClientFamily[]>) => {
        state.clientsFamily = action.payload;
        state.clientFamilyLoading = false;
      },
    )
    .addCase(getClientsFamily.rejected, (state: ClientState, action: any) => {
      state.clientsFamily = [];
      state.error = action.error;
      state.clientFamilyLoading = false;
      toast.error(action.error.message);
    })
    // updateClientClinicalStatus()
    .addCase(updateClientClinicalStatus.pending, (state: ClientState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(updateClientClinicalStatus.fulfilled, (state: ClientState, action: any) => {
      const { clientId } = action.meta.arg;
      const { clinical_status, clinical_status_reason } = action.payload;
      if (state.selected?.id === clientId) {
        state.selected = { ...state.selected, ...action.payload };
      }
      state.list =
        state.list?.map((client) => {
          if (client?.id === clientId) {
            return {
              ...client,
              clinical_status,
              clinical_status_reason,
            };
          }
          return client;
        }) ?? [];
      state.allClientsList =
        state.allClientsList?.map((client) => {
          if (client?.id === clientId) {
            return {
              ...client,
              clinical_status,
              clinical_status_reason,
            };
          }
          return client;
        }) ?? [];
      state.loading = false;
      toast.success(`Clinical status of client updated`);
    })
    .addCase(updateClientClinicalStatus.rejected, (state: any, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(`Cannot update the Clinical status of client`);
    })
    // For client post
    .addCase(createClient.pending, (state: ClientState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(createClient.fulfilled, (state: ClientState, action: PayloadAction<IClient>) => {
      state.selected = action.payload;
      state.loading = false;
      toast.success('New Client Created', { toastId: 'createClientSuccess' });
    })
    .addCase(createClient.rejected, (state: any, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message, { toastId: 'createClientError' });
    })
    // For getting policy prior authorization details
    .addCase(getPolicyPriorAuthorizationDetails.pending, (state: ClientState) => {
      state.paDetailsLoading = true;
      state.selectedPolicyPriorAuthorizationDetails = null;
      state.error = null;
    })
    .addCase(
      getPolicyPriorAuthorizationDetails.fulfilled,
      (state: ClientState, action: PayloadAction<IPriorAuthorizationDetail>) => {
        state.selectedPolicyPriorAuthorizationDetails = action.payload;
        state.paDetailsLoading = false;
      },
    )
    .addCase(
      getPolicyPriorAuthorizationDetails.rejected,
      (state: ClientState, action: { error: SerializedError }) => {
        state.error = action.error;
        state.selectedPolicyPriorAuthorizationDetails = null;
        state.paDetailsLoading = false;
        toast.error(action.error.message);
      },
    )
    // For client to billing facility assignment
    .addCase(assignFacility.pending, (state: any) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(assignFacility.fulfilled, (state: any, action: PayloadAction<any>) => {
      state.assignedBillingFacility = action.payload;
      state.loading = false;
      toast.success('Client assigned to facility successfully');
    })
    .addCase(assignFacility.rejected, (state: any, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // For cline to billing facility update
    .addCase(updateFacilityAssignment.pending, (state: any) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(updateFacilityAssignment.fulfilled, (state: any, action: PayloadAction<any>) => {
      state.assignedBillingFacility = action.payload;
      state.loading = false;
      toast.success('Facility updated successfully');
    })
    .addCase(updateFacilityAssignment.rejected, (state: any, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // For delete client facility assignment
    .addCase(deleteFacilityAssignment.pending, (state: any) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(deleteFacilityAssignment.fulfilled, (state: any) => {
      state.loading = false;
      toast.success('Facility assignment deleted successfully');
    })
    .addCase(deleteFacilityAssignment.rejected, (state: any, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // For client payor assignment
    .addCase(assignPayor.pending, (state: any) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(assignPayor.fulfilled, (state: any, action: PayloadAction<any>) => {
      state.assignedPayor = action.payload;
      state.loading = false;
      toast.success('Client assigned to payor successfully');
    })
    .addCase(assignPayor.rejected, (state: any, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })
    //for client policy update
    .addCase(editPolicy.pending, (state: any) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(editPolicy.fulfilled, (state: any, action: PayloadAction<any>) => {
      state.assignedPayor = action.payload;
      state.loading = false;
      toast.success('Policy updated successfully');
    })
    .addCase(editPolicy.rejected, (state: any, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // For Client Facilities Mapping
    .addCase(getClientFacilitiesMapping.pending, (state: ClientState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      getClientFacilitiesMapping.fulfilled,
      (state: ClientState, action: PayloadAction<IClientFacilityMap[]>) => {
        state.clientFacilitiesMapping = action.payload;
        state.loading = false;
      },
    )
    .addCase(getClientFacilitiesMapping.rejected, (state: ClientState, action: any) => {
      state.clientFacilitiesMapping = [];
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })
    // For get selected client
    .addCase(getClient.pending, (state: ClientState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(getClient.fulfilled, (state: ClientState, action: PayloadAction<any>) => {
      state.selected = action.payload;
      state.selectedClientDetailsRequestClientId = null;
      state.loading = false;
    })
    .addCase(getClient.rejected, (state: ClientState, action: any) => {
      state.selected = null;
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })
    .addCase(
      getSelectedClientDetails.pending,
      (state: ClientState, action: IClientDetailsPendingAction) => {
        state.error = null;
        state.selectedClientDetailsRequestClientId = action.meta.arg.clientId;
      },
    )
    .addCase(
      getSelectedClientDetails.fulfilled,
      (state: ClientState, action: IClientDetailsFulfilledAction) => {
        if (state.selectedClientDetailsRequestClientId !== action.meta.arg.clientId) return;
        state.selected = action.payload;
        state.selectedClientDetailsRequestClientId = null;
      },
    )
    .addCase(
      getSelectedClientDetails.rejected,
      (state: ClientState, action: IClientDetailsRejectedAction) => {
        if (state.selectedClientDetailsRequestClientId !== action.meta.arg.clientId) return;
        state.selectedClientDetailsRequestClientId = null;
        state.error = action.error;
        toast.error(action.error.message);
      },
    )
    // For Client Payors Mapping
    .addCase(getClientPayorsMapping.pending, (state: ClientState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      getClientPayorsMapping.fulfilled,
      (state: ClientState, action: PayloadAction<IClientPolicyMap[]>) => {
        state.clientPayorsMapping = action.payload;
        state.loading = false;
      },
    )
    .addCase(getClientPayorsMapping.rejected, (state: ClientState, action: any) => {
      state.clientPayorsMapping = [];
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })
    // For Client specific Payors mapping
    .addCase(getClientSpecificPayorsMapping.pending, (state: ClientState) => {
      state.selectedClientPolicyLoading = true;
      state.error = null;
    })
    .addCase(
      getClientSpecificPayorsMapping.fulfilled,
      (state: ClientState, action: PayloadAction<IClientPolicyMap[]>) => {
        state.selectedClientPlans = action.payload;
        state.selectedClientPolicyLoading = false;
      },
    )
    .addCase(getClientSpecificPayorsMapping.rejected, (state: ClientState, action: any) => {
      state.selectedClientPlans = [];
      state.error = action.error;
      state.selectedClientPolicyLoading = false;
      toast.error(action.error.message);
    })
    // For Client specific Facilities mapping
    .addCase(getClientSpecificFacilityMapping.pending, (state: ClientState) => {
      state.facilityAssignmentsLoading = true;
      state.error = null;
    })
    .addCase(
      getClientSpecificFacilityMapping.fulfilled,
      (state: ClientState, action: PayloadAction<IClientFacilityMap[]>) => {
        state.selectedClientFacility = action.payload;
        state.facilityAssignmentsLoading = false;
      },
    )
    .addCase(getClientSpecificFacilityMapping.rejected, (state: ClientState, action: any) => {
      state.selectedClientFacility = [];
      state.error = action.error;
      state.facilityAssignmentsLoading = false;
      toast.error(action.error.message);
    })
    // For fetching authorization
    .addCase(getAuthorization.pending, (state: ClientState) => {
      state.selectedClintAuthloading = true;
      state.error = null;
    })
    .addCase(
      getAuthorization.fulfilled,
      (state: ClientState, action: PayloadAction<IClientAuthorization[]>) => {
        state.selectedClintAuthloading = false;
        state.selectedClientAuthorization = action.payload;
      },
    )
    .addCase(getAuthorization.rejected, (state: ClientState, action: any) => {
      state.error = action.error;
      state.selectedClintAuthloading = false;
      state.selectedClientAuthorization = [];
      toast.error(action.error.message);
    })
    // For creating authorization
    .addCase(createAuthorization.pending, (state: ClientState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      createAuthorization.fulfilled,
      (state: ClientState) => {
        state.loading = false;
        toast.success('Authorization created');
      },
    )
    .addCase(createAuthorization.rejected, (state: ClientState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })
    // For deactivating client facility assignment
    .addCase(deactiveClientFacilityAssignment.pending, (state: ClientState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      deactiveClientFacilityAssignment.fulfilled,
      (state: ClientState) => {
        state.loading = false;
        toast.success('Client facility deactivated successfully');
      },
    )
    .addCase(deactiveClientFacilityAssignment.rejected, (state: ClientState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })
    // For promoting client
    .addCase(promoteClient.pending, (state: ClientState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(promoteClient.fulfilled, (state: ClientState) => {
      state.loading = false;
      toast.success('Client promoted successfully');
    })
    .addCase(promoteClient.rejected, (state: ClientState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // For reset assessment allocation
    .addCase(resetAssessmentAllocation.pending, (state: ClientState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(resetAssessmentAllocation.fulfilled, (state: ClientState) => {
      state.loading = false;
      toast.success('Assessment allocation reset successfully');
    })
    .addCase(resetAssessmentAllocation.rejected, (state: ClientState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // For updating client
    .addCase(updateClient.pending, (state: ClientState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(updateClient.fulfilled, (state: ClientState, action: any) => {
      state.loading = false;
      state.selected = action.payload;
      toast.success('Client updated successfully');
    })
    .addCase(updateClient.rejected, (state: ClientState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // fetchClientModifierMatrix()
    // Guard by requestId (the latest dispatched fetch) so any stale/out-of-order response is
    // dropped — including a same-client refetch that resolves after a newer one. On success we
    // also record which clientId the stored data belongs to, so the hook never surfaces another
    // client's rows; on failure we flag an error so the section can show an error state instead
    // of an empty (misleading) one.
    .addCase(
      fetchClientModifierMatrix.pending,
      (state: ClientState, action: { meta: { requestId: string } }) => {
        state.modifierMatrixRequestId = action.meta.requestId;
        state.modifierMatrixLoading = true;
        state.modifierMatrixError = false;
        state.error = null;
      },
    )
    .addCase(
      fetchClientModifierMatrix.fulfilled,
      (
        state: ClientState,
        action: PayloadAction<
          IClientModifierMatrixRow[],
          string,
          { requestId: string; arg: IFetchClientModifierMatrixArg }
        >,
      ) => {
        if (action.meta.requestId !== state.modifierMatrixRequestId) return;
        state.selectedClientModifierMatrix = action.payload;
        state.modifierMatrixClientId = action.meta.arg.clientId ?? null;
        state.modifierMatrixLoading = false;
      },
    )
    .addCase(
      fetchClientModifierMatrix.rejected,
      (
        state: ClientState,
        action: {
          error: SerializedError;
          meta: { requestId: string; arg: IFetchClientModifierMatrixArg };
        },
      ) => {
        if (action.meta.requestId !== state.modifierMatrixRequestId) return;
        state.selectedClientModifierMatrix = [];
        state.modifierMatrixClientId = null;
        state.error = action.error;
        state.modifierMatrixError = true;
        state.modifierMatrixLoading = false;
        toast.error(action.error.message);
      },
    )

    // createClientModifierMatrixRow()
    // Modifier mutations must NOT toggle the page-wide `state.loading` (ClientDetails uses it
    // for its basic-info skeleton/disabled controls) — the section drives its own `isSaving`.
    .addCase(createClientModifierMatrixRow.pending, (state: ClientState) => {
      state.error = null;
    })
    .addCase(createClientModifierMatrixRow.fulfilled, () => {
      toast.success('Modifier matrix row created successfully', {
        toastId: 'client-modifier-matrix-create-success',
      });
    })
    .addCase(
      createClientModifierMatrixRow.rejected,
      (state: ClientState, action: { error: SerializedError }) => {
        state.error = action.error;
        toast.error(state.error.message, {
          toastId: 'client-modifier-matrix-create-error',
        });
      },
    )

    // updateClientModifierMatrixRow()
    .addCase(updateClientModifierMatrixRow.pending, (state: ClientState) => {
      state.error = null;
    })
    .addCase(updateClientModifierMatrixRow.fulfilled, () => {
      toast.success('Modifier matrix row updated successfully', {
        toastId: 'client-modifier-matrix-update-success',
      });
    })
    .addCase(
      updateClientModifierMatrixRow.rejected,
      (state: ClientState, action: { error: SerializedError }) => {
        state.error = action.error;
        toast.error(state.error.message, {
          toastId: 'client-modifier-matrix-update-error',
        });
      },
    )

    // deleteClientModifierMatrixRow()
    .addCase(deleteClientModifierMatrixRow.pending, (state: ClientState) => {
      state.error = null;
    })
    .addCase(deleteClientModifierMatrixRow.fulfilled, () => {
      toast.success('Modifier matrix row deleted successfully', {
        toastId: 'client-modifier-matrix-delete-success',
      });
    })
    .addCase(
      deleteClientModifierMatrixRow.rejected,
      (state: ClientState, action: { error: SerializedError }) => {
        state.error = action.error;
        toast.error(state.error.message, {
          toastId: 'client-modifier-matrix-delete-error',
        });
      },
    );
}

export const clientActions = { ...slice.actions, ...extraActions };
export const clientReducer = slice.reducer;
